import { TuettuKieli } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { KirjanpitajaTiimiAvaimet, KirjanpitajanNimitiedot, KirjanpitajanToimipiste } from '../model/kirjanpitaja'
import { KirjanpitajanTiimiService } from '../service/kirjanpitajan-tiimi.service'
import { KirjanpitajanToimipisteService } from './kirjanpitajan-toimipiste.service'

export class KirjanpitajanAllekirjoitusService {
  private _hallintoToimipisteAvain = 'bEY5mLBGcVYhRYxz2hC1'

  constructor(
    private _toimipisteService: KirjanpitajanToimipisteService,
    private _kirjanpitajanTiimiService: KirjanpitajanTiimiService,
    private _dateService: DateService,
  ) {}

  private async _onkoHallintoEnsisijainenTiimi(kirjanpitajaAvain: string) {
    const tiimit = await this._kirjanpitajanTiimiService.annaKirjanpitajanTiimit(kirjanpitajaAvain, this._dateService.currentNumberDate())
    return tiimit.some(t => t.primary && t.tiimi.avain === KirjanpitajaTiimiAvaimet.GENOVA)
  }

  private _muodostaAllekirjoitusTeksti(kirjanpitaja: KirjanpitajanNimitiedot): string {
    let teksti = `${kirjanpitaja.etunimi} ${kirjanpitaja.sukunimi}`

    if (kirjanpitaja?.tutkinnot?.klt) {
      teksti += ', KLT'
    }

    return teksti
  }

  async getSignatureHtml(kirjanpitaja: KirjanpitajanNimitiedot, kieli: TuettuKieli): Promise<string> {
    const defaultLemonImageUrl = 'https://storage.googleapis.com/lemon-public-images/lemon-tree-logo.jpg'

    // Holvi
    if (kirjanpitaja.avain === 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2') {
      return this._getHolviSignatureTemplate(kieli)
    }

    // Hallinto
    if (await this._onkoHallintoEnsisijainenTiimi(kirjanpitaja.avain)) {
      return this.getHallintoSignatureTemplate(defaultLemonImageUrl, kieli)
    }

    // Normaali
    const toimipiste = this._toimipisteService.annaToimipiste(kirjanpitaja.toimipisteAvain)
    return this._getRegularSignatureTemplate(kirjanpitaja, toimipiste, defaultLemonImageUrl, kieli)
  }

  async getSignatureText(kirjanpitaja: KirjanpitajanNimitiedot, kieli: TuettuKieli, htmlStripper: { getTextEmailFromHtmlEmail: (tekstiHtml: string) => Promise<string> }): Promise<string> {
    // Holvi
    if (kirjanpitaja.avain === 'QgPvtcCjoOdf6Zg7lgMwqLWp2BG2') {
      return `${kieli === 'en' ? 'Holvi Zen Accounting Team' : 'Holvi Zen kirjanpitotiimi'}`
    }

    // Hallinto
    if (await this._onkoHallintoEnsisijainenTiimi(kirjanpitaja.avain)) {
      const hallintoToimipiste = this._toimipisteService.annaToimipiste(this._hallintoToimipisteAvain)
      return `${kieli === 'en' ? 'Administrative Team' : 'Hallintotiimi'}
Tilitoimisto Lemon Tree Oy
010 517 6020 | info@lemontree.fi
www.lemontree.fi
${hallintoToimipiste.katuosoite}, ${hallintoToimipiste.postinro} ${hallintoToimipiste.kaupunki}`
    }

    // Normaali
    const toimipiste = this._toimipisteService.annaToimipiste(kirjanpitaja.toimipisteAvain)
    const lomaviesti = kirjanpitaja.lomaviesti?.[kieli] ? await htmlStripper.getTextEmailFromHtmlEmail(kirjanpitaja.lomaviesti[kieli]) : ''

    // Notice that the used lomaviesti contains two rivinvaihto if it has text...
    const usedLomaviesti = lomaviesti
      ? `${lomaviesti}

`
      : ''

    // ...and for that reason lomaviesti has no space in it.
    return `${usedLomaviesti}${this._muodostaAllekirjoitusTeksti(kirjanpitaja)}
Tilitoimisto Lemon Tree Oy
${kirjanpitaja?.puhelin ? kirjanpitaja.puhelin + ' |' : ''}  ${kirjanpitaja.email}
www.lemontree.fi
${toimipiste.katuosoite}, ${toimipiste.postinro} ${toimipiste.kaupunki}`
  }

  getHallintoSignatureText(kieli: TuettuKieli) {
    const hallintoToimipiste = this._toimipisteService.annaToimipiste(this._hallintoToimipisteAvain)
    const signature = `${kieli === 'en' ? 'Administrative Team' : 'Hallintotiimi'}
Tilitoimisto Lemon Tree Oy
010 517 6020 | info@lemontree.fi
www.lemontree.fi
${hallintoToimipiste.katuosoite}, ${hallintoToimipiste.postinro} ${hallintoToimipiste.kaupunki}`
    return signature
  }

  private _getRegularSignatureTemplate(kirjanpitaja: KirjanpitajanNimitiedot, toimipiste: KirjanpitajanToimipiste, defaultLemonImageUrl: string, kieli: TuettuKieli) {
    const allekirjoitusTeksti = this._muodostaAllekirjoitusTeksti(kirjanpitaja)
    const textStart = `<div style="padding: 8px 8px 18px 8px; margin-bottom: 8px;">
${kirjanpitaja.lomaviesti?.[kieli] ?? ''}
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border:0; padding:0; margin: 0; color:#000;">
  <tr>
    <!-- Vasen kuvasarake -->
    <td width="93" valign="middle" style="width:93px; padding-right:10px; vertical-align: middle;">
      <img src="${kirjanpitaja.signatureImageUrl || defaultLemonImageUrl}"
            width="93" height="92" alt="${kieli === 'en' ? 'Accountant image' : 'Kirjanpitäjän kuva'}"
            style="display:block; border:0; border-radius:50%; width:93px; height:92px;">
    </td>

    <!-- Oikea sisältö (sininen pystyviiva) -->
    <td valign="middle" style="padding-left:10px; border-left:1px solid #1BA2EB; vertical-align: middle;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border: 0;font-size:12px;text-align:initial;font-stretch:normal;line-height:normal;padding:0px 10px; color:#646464;">
        <tr>
          <td style="padding-bottom:5px;">
            <span style="font-weight:bold; color:#241f5d;">${allekirjoitusTeksti}</span><br>
            <span style="color:#646464;">Tilitoimisto Lemon Tree Oy</span>
          </td>
        </tr>

        <tr>
          <td>`

    const puhelinText = `
            <span style="color:#8d8d8d;">${kirjanpitaja.puhelin}</span>
            &nbsp;<span style="color:#1BA2EB;">|</span>&nbsp;`
    const textEnd = `<span style="color:#8d8d8d;">${kirjanpitaja.email}</span>
          </td>
        </tr>

        <tr>
          <td>
            <span style="color:#8d8d8d;">www.lemontree.fi</span>
          </td>
        </tr>

        <tr>
          <td>
            <span style="color:#8d8d8d;">${toimipiste.katuosoite}, ${toimipiste.postinro} ${toimipiste.kaupunki}</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table></div>`

    return textStart + (kirjanpitaja.puhelin ? puhelinText : '') + textEnd
  }

  private _getHolviSignatureTemplate(kieli: TuettuKieli) {
    const html = `<div style="padding: 8px 8px 18px 8px; margin-bottom: 8px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border:0; padding:0; margin: 0; color:#000;">
  <tr>
    <!-- Vasen kuvasarake -->
    <td width="93" valign="middle" style="width:93px; padding-right:10px; vertical-align: middle;">
      <img src="https://storage.googleapis.com/lemon-public-images/holvi_logo.png" width="93" height="92" alt="Holvi" style="width: 93px;height: 92px; border: 0;">
    </td>

    <!-- Oikea sisältö (sininen pystyviiva) -->
    <td valign="middle" style="padding-left:10px; border-left:1px solid #1BA2EB; vertical-align: middle;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border: 0;font-size:12px;text-align:initial;font-stretch:normal;line-height:normal;padding:0px 10px; color:#646464;">
        <tr>
          <td>
            <span style="font-weight:bold; color:#22272c;">${kieli === 'en' ? 'Holvi Zen Accounting Team' : 'Holvi Zen kirjanpitotiimi'}</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table></div>`

    return html
  }

  getHallintoSignatureTemplate(defaultLemonImageUrl: string, kieli: TuettuKieli, center?: boolean) {
    const toimipiste = this._toimipisteService.annaToimipiste('bEY5mLBGcVYhRYxz2hC1')
    const html = `<div style="padding: 8px 8px 18px 8px; margin-bottom: 8px;">
<table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border:0; padding:0; ${center ? 'margin: 0 auto' : 'margin: 0'}; color:#000;">
  <tr>
    <!-- Vasen kuvasarake -->
    <td width="93" valign="middle" style="width:93px; padding-right:10px; vertical-align: middle;">
      <img src=${defaultLemonImageUrl} width="93" height="92" alt="Lemon Tree" style="width: 93px;height: 92px; border: 0;">
    </td>

    <!-- Oikea sisältö (sininen pystyviiva) -->
    <td valign="middle" style="padding-left:10px; border-left:1px solid #1BA2EB; vertical-align: middle;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="border: 0;font-size:12px;text-align:initial;font-stretch:normal;line-height:normal;padding:0px 10px; color:#646464;">
        <tr>
          <td style="padding-bottom:5px;">
            <span style="font-weight:bold; color:#22272c;">${kieli === 'en' ? 'Administrative Team' : 'Hallintotiimi'}</span><br>
            <span style="color:#646464;">Tilitoimisto Lemon Tree Oy</span>
          </td>
        </tr>

        <tr>
          <td>
            <span style="color:#8d8d8d;">010 517 6020</span>
            &nbsp;<span style="color:#1BA2EB;">|</span>&nbsp;
            <span style="color:#8d8d8d;">info@lemontree.fi</span>
          </td>
        </tr>

        <tr>
          <td>
            <span style="color:#8d8d8d;">www.lemontree.fi</span>
          </td>
        </tr>

        <tr>
          <td>
            <span style="color:#8d8d8d;">${toimipiste.katuosoite}, ${toimipiste.postinro} ${toimipiste.kaupunki}</span>
          </td>
        </tr>
      </table>
    </td>
  </tr>
</table></div>`

    return html
  }
}
